package org.example.project.engineslot

import org.example.project.dashboard.DashboardTheme
import org.example.project.settings.UISettings
import org.example.project.service.ZustandManager
import org.jetbrains.compose.web.css.*

object SlotStyles : StyleSheet() {
    private val tabularHeaderBg = Color("#40454D")
    private val slotReservedBorder = Color(ZustandManager.Slot.colorHexFor(ZustandManager.Slot.RESERVIERT))

    val topPanel by style {
        width(100.percent)
        padding(0.px)
        margin(0.px)
    }

    val screenRoot by style {
        width(100.percent)
        height(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(16.px)
        minHeight(0.px)
    }

    val glassPanel by style {
        backgroundColor(Color("rgba(255,255,255,0.03)"))
        property("border", "1px solid rgba(255,255,255,0.18)")
        borderRadius(20.px)
        property("box-shadow", "0 18px 40px ${DashboardTheme.SURFACE_SHADOW}")
        boxSizing("border-box")
    }

    val hoverPanel by style {
        backgroundColor(Color("#232831"))
        property("border", "1px solid rgba(255,255,255,0.18)")
        borderRadius(20.px)
        property("box-shadow", "0 18px 40px ${DashboardTheme.SURFACE_SHADOW}")
        boxSizing("border-box")
    }

    val compactPanel by style {
        backgroundColor(Color("rgba(255,255,255,0.03)"))
        property("border", "1px solid rgba(255,255,255,0.18)")
        borderRadius(14.px)
        boxSizing("border-box")
    }

    val sectionTitle by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(20.px)
        fontWeight("700")
    }

    val popupTitleCentered by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(22.px)
        fontWeight("700")
        textAlign("center")
        width(100.percent)
    }

    val topHeadline by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(18.px)
        fontWeight("700")
        textAlign("center")
        width(100.percent)
    }

    val topAddress by style {
        color(DashboardTheme.TEXT_MUTED)
        fontSize(12.px)
        textAlign("center")
        width(100.percent)
    }

    val caption by style {
        color(DashboardTheme.TEXT_MUTED)
        fontSize(13.px)
        lineHeight("1.45")
    }

    val label by style {
        color(DashboardTheme.TEXT_MUTED)
        fontSize(12.px)
        fontWeight("700")
        property("text-transform", "uppercase")
        letterSpacing(0.5.px)
    }

    val popupFieldLabel by style {
        color(DashboardTheme.TEXT_MUTED)
        fontSize(12.px)
        fontWeight("700")
        letterSpacing(0.2.px)
    }

    val value by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(14.px)
        fontWeight("600")
    }

    val bookingHeaderRow by style {
        width(100.percent)
        display(DisplayStyle.Grid)
        property("grid-template-columns", "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)")
        alignItems(AlignItems.Center)
        gap(8.px)
        minWidth(0.px)
    }

    val bookingHeaderStat by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
        minWidth(0.px)
    }

    val bookingHeaderStatStart by style {
        justifyContent(JustifyContent.FlexStart)
    }

    val bookingHeaderStatCenter by style {
        justifyContent(JustifyContent.Center)
    }

    val bookingHeaderStatEnd by style {
        justifyContent(JustifyContent.FlexEnd)
    }

    val bookingHeaderIcon by style {
        width(18.px)
        height(18.px)
        flexShrink(0)
        property("filter", "brightness(0) invert(1)")
    }

    val bookingHeaderMeta by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(13.px)
        fontWeight("600")
        whiteSpace("nowrap")
    }

    val bookingHeaderDividerLine by style {
        width(100.percent)
        height(1.px)
        backgroundColor(Color("rgba(255,255,255,0.14)"))
        margin(2.px, 0.px, 4.px, 0.px)
    }

    val bookingSummaryRow by style {
        width(100.percent)
        display(DisplayStyle.Grid)
        property("grid-template-columns", "20px minmax(0, 1fr)")
        columnGap(8.px)
        alignItems(AlignItems.Center)
        minWidth(0.px)
    }

    val bookingSummaryIconCell by style {
        width(20.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        alignSelf(AlignSelf.Start)
    }

    val bookingSummaryLabel by style {
        color(DashboardTheme.TEXT_MUTED)
        fontSize(12.px)
        fontWeight("600")
        lineHeight("1.35")
        whiteSpace("nowrap")
    }

    val bookingSummaryValue by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(13.px)
        fontWeight("600")
        lineHeight("1.35")
        minWidth(0.px)
        whiteSpace("nowrap")
        overflow("hidden")
        property("text-overflow", "ellipsis")
    }

    val bookingSummaryIcon by style {
        width(14.px)
        height(14.px)
        flexShrink(0)
        property("filter", "brightness(0) invert(1)")
    }

    val bookingTopDetailsRow by style {
        width(100.percent)
        display(DisplayStyle.Grid)
        property("grid-template-columns", "minmax(0, 1fr) minmax(0, auto)")
        columnGap(8.px)
        alignItems(AlignItems.Center)
        minWidth(0.px)
    }

    val bookingTopDetailsCell by style {
        minWidth(0.px)
        display(DisplayStyle.Grid)
        property("grid-template-columns", "20px minmax(0, 1fr)")
        columnGap(8.px)
        alignItems(AlignItems.Center)
        overflow("hidden")
    }

    val bookingTopDetailsCellSpacer by style {
        minWidth(0.px)
    }

    val bookingTopDetailsCellRight by style {
        property("grid-template-columns", "16px minmax(0, auto)")
        justifyContent(JustifyContent.FlexEnd)
    }

    val gridScroll by style {
        flex(1)
        width(100.percent)
        overflowY("auto")
        overflowX("auto")
        borderRadius(12.px)
        property("border", "1px solid rgba(255,255,255,0.12)")
        property("box-shadow", "0 10px 26px rgba(0,0,0,0.14)")
        property("overscroll-behavior", "contain")
        property("scroll-snap-type", "none")
        property("scrollbar-width", "thin")
        property("scrollbar-color", "rgba(56, 62, 74, 0.98) transparent")

        self + "::-webkit-scrollbar" style {
            width(12.px)
            height(12.px)
            backgroundColor(Color.transparent)
        }

        self + "::-webkit-scrollbar-track" style {
            backgroundColor(Color.transparent)
        }

        self + "::-webkit-scrollbar-thumb" style {
            borderRadius(999.px)
            property("background", "linear-gradient(180deg, rgba(72, 79, 92, 0.98) 0%, rgba(46, 52, 62, 0.98) 100%)")
            property("border", "1px solid rgba(255,255,255,0.10)")
        }

        self + "::-webkit-scrollbar-corner" style {
            backgroundColor(Color.transparent)
        }
    }

    val gridWrapper by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        padding(8.px)
        gap(8.px)
        overflow("hidden")
        height(100.percent)
        minHeight(0.px)
        property("box-sizing", "border-box")
    }

    val tableGrid by style {
        backgroundColor(Color.transparent)
    }

    val stickyHeaderRow by style {
        position(Position.Sticky)
        top(0.px)
        property("z-index", "12")
    }

    val tableHeaderCell by style {
        minHeight(42.px)
        padding(8.px, 10.px)
        property("border-bottom", "1px solid rgba(255,255,255,0.14)")
        property("border-right", "1px solid rgba(255,255,255,0.14)")
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        backgroundColor(tabularHeaderBg)
    }

    val tableHeaderLabel by style {
        color(DashboardTheme.TEXT_MUTED)
        fontSize(10.px)
        fontWeight("700")
        property("text-transform", "uppercase")
        letterSpacing(0.3.px)
    }

    val tableAxisLabel by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(13.px)
        fontWeight("700")
        letterSpacing(0.px)
    }

    val tableRowHeaderCell by style {
        height(120.px)
        minHeight(120.px)
        padding(8.px, 4.px)
        property("border-right", "1px solid rgba(255,255,255,0.14)")
        property("border-bottom", "1px solid rgba(255,255,255,0.14)")
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        backgroundColor(tabularHeaderBg)
        position(Position.Sticky)
        left(0.px)
        property("z-index", "11")
    }

    val tableRowHeaderValue by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(13.px)
        fontWeight("600")
    }

    val slotCell by style {
        height(120.px)
        minHeight(120.px)
        padding(0.px)
        boxSizing("border-box")
        overflow("hidden")
    }

    val slotCellCard by style {
        width(100.percent)
        height(120.px)
        maxHeight(120.px)
        minHeight(120.px)
        padding(10.px)
        backgroundColor(Color("rgba(255,255,255,0.03)"))
        property("border", "1px solid rgba(255,255,255,0.14)")
        borderRadius(0.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
        overflow("hidden")
    }

    val slotCellActive by style {
        backgroundColor(Color("rgba(255,255,255,0.03)"))
        property("border", "1px solid $slotReservedBorder")
    }

    val slotCellEditable by style {
        property("box-shadow", "inset 0 0 0 1px rgba(255,255,255,0.08)")
    }

    val slotCellDisabled by style {
        backgroundColor(Color.transparent)
        property("border-color", "transparent")
        property("box-shadow", "none")
    }

    val slotCellSelected by style {
        property("outline", "2px solid ${UISettings.ACCENT}")
        property("outline-offset", "-2px")
    }

    val slotCellInBooking by style {
        property("border", "1px solid $slotReservedBorder")
        backgroundColor(Color("rgba(66,165,245,0.08)"))
        property("box-shadow", "none")
    }

    val slotInBookingLabel by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(14.px)
        fontWeight("700")
        lineHeight("1.2")
    }

    val metricValue by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(24.px)
        fontWeight("700")
    }

    val datePagerPlain by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(16.px)
    }

    val datePagerArrow by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(18.px)
        fontWeight("700")
        cursor("pointer")
        property("transition", "color 0.14s ease, text-shadow 0.14s ease, transform 0.12s ease")
    }

    val datePagerArrowActive by style {
        color(UISettings.ACCENT)
        property("text-shadow", "0 0 10px rgba(255,0,0,0.35)")
        property("transform", "translateY(1px)")
    }

    val datePagerValue by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(16.px)
        fontWeight("700")
        whiteSpace("nowrap")
    }

    val actionBarRow by style {
        width(100.percent)
        display(DisplayStyle.Grid)
        property("grid-template-columns", "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)")
        alignItems(AlignItems.Center)
        gap(0.px)
        padding(6.px, 0.px, 0.px, 0.px)
        minHeight(34.px)
    }

    val actionBarLeft by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        paddingLeft(16.px)
        paddingRight(16.px)
        minWidth(0.px)
    }

    val actionBarCenter by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        paddingLeft(16.px)
        paddingRight(16.px)
        minWidth(0.px)
    }

    val actionBarRight by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        paddingLeft(16.px)
        paddingRight(16.px)
        minWidth(0.px)
    }

    val calendarIcon by style {
        width(20.px)
        height(20.px)
    }

    val toolbarIconButton by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        width(28.px)
        height(28.px)
        cursor("pointer")
        property("transition", "filter 0.14s ease, transform 0.12s ease, box-shadow 0.14s ease")
    }

    val toolbarIconButtonActive by style {
        property("filter", "drop-shadow(0 0 8px rgba(255,0,0,0.28))")
        property("transform", "translateY(1px)")
    }

    val toolbarAction by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
        cursor("pointer")
        padding(0.px, 10.px)
        minWidth(0.px)
        property("user-select", "none")
        color(DashboardTheme.TEXT_LIGHT)
        backgroundColor(Color.transparent)
        border(0.px)
        property("box-shadow", "none")
    }

    val toolbarActionIcon by style {
        width(18.px)
        height(18.px)
        marginRight(6.px)
    }

    val toolbarActionText by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(13.px)
        fontWeight("600")
        whiteSpace("nowrap")
    }

    val toolbarActionSeparator by style {
        width(1.px)
        height(16.px)
        backgroundColor(Color("rgba(255,255,255,0.2)"))
    }

    val calendarPopupBody by style {
        width(100.percent)
        height(100.percent)
        padding(28.px, 18.px, 18.px, 18.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
    }

    val calendarPopupContent by style {
        width(300.px)
        maxWidth(300.px)
        flexShrink(0)
    }

    val popupFieldGrid by style {
        width(420.px)
        maxWidth(100.percent)
        property("margin", "0 auto")
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
        columnGap(16.px)
        rowGap(10.px)
        property("justify-items", "center")
    }

    val popupFieldGridSpacer by style {
        width(100.percent)
        property("grid-column", "1 / -1")
        height(20.px)
    }

    val popupTimeInput by style {
        width(158.px)
        maxWidth(100.percent)

        self + "::-webkit-calendar-picker-indicator" style {
            property("filter", "brightness(0) invert(1)")
            opacity(1)
            cursor("pointer")
        }

        self + "::-webkit-clear-button" style {
            property("filter", "brightness(0) invert(1)")
        }

        self + "::-webkit-inner-spin-button" style {
            property("filter", "brightness(0) invert(1)")
        }
    }

    val popupCompactInput by style {
        width(158.px)
        maxWidth(100.percent)
    }

    val popupBlock by style {
        width(420.px)
        maxWidth(100.percent)
        property("margin", "0 auto")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(30.px)
        alignItems(AlignItems.Center)
    }

    val popupButtonCell by style {
        width(158.px)
        maxWidth(100.percent)
    }
}

fun StyleScope.slotInputStyle() {
    width(100.percent)
    minHeight(36.px)
    padding(0.px, 12.px)
    boxSizing("border-box")
    color(DashboardTheme.TEXT_LIGHT)
    backgroundColor(Color("rgba(255,255,255,0.03)"))
    property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
    borderRadius(8.px)
}
